// 選んでいるパーツの枠・キャラの上を押して選ぶ（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { C } from './core/model.js';
import { SIZE, ZOOM_MAX } from './config.js';
import { catByKey, layerG } from './store.js';
import { $, keyOf } from './util.js';
import { selectCategory } from './categories.js';
import { pickKey } from './merge.js';
import { canMove, updateTools } from './tools.js';
import { fitWithoutContainerQueries } from './compat.js';
import type { Category } from './types.js';
import { setRotate, setScale } from './core/ops.js';

type Rect = { left: number; top: number; right: number; bottom: number };
// つまみを動かしている間の値（回すつまみ rot は a0・v0・rev、大きさのつまみは d0・z0・tx0・ty0・m）
type BoxDrag = { id: number; rot?: boolean; cx: number; cy: number; a0: number; v0: number; rev: boolean; d0: number; z0: number; tx0: number; ty0: number; m: { x: number; y: number } | null };
export function partRect(key: string): Rect | null {
  let cat = catByKey[key], r: Rect | null = null;
  if (!cat) return null;
  cat.LAYER.forEach((n) => {
    const g = layerG[n];
    if (!g || !g.firstElementChild) return;
    const b = g.getBoundingClientRect();
    if (!b.width && !b.height) return;
    r = r ? { left: Math.min(r.left, b.left), top: Math.min(r.top, b.top), right: Math.max(r.right, b.right), bottom: Math.max(r.bottom, b.bottom) }
      : { left: b.left, top: b.top, right: b.right, bottom: b.bottom };
  });
  return r;
}
// パーツのまん中（レイヤーの g の座標。拡大の分は入っている。つまみで大きさを変えるときの基準。回転の中心と同じ charat.draw.js の rotateCenter）。
// パーツが入っていなければ null
export function layerCenter(cat: Category) {
  const layers = cat.LAYER.map((n) => layerG[n]).filter((g) => g && g.firstElementChild);
  if (!layers.length) return null;
  const c = charatDraw.rotateCenter(layers);
  return { x: c.cx, y: c.cy };
}
export function scheduleBox() { if (!S.boxRaf) S.boxRaf = requestAnimationFrame(updateBox); }
export function updateBox() {
  S.boxRaf = 0;
  const box = $('partBox'), cat = catByKey[S.selKey], st = C.state[S.selKey];
  const moving = S.moveMode && canMove(cat, st);
  const r = (moving || S.boxFlash) && S.selKey ? partRect(S.selKey) : null;
  if (!r) { box.hidden = true; return; }
  const host = $('stageBox').getBoundingClientRect(), stage = $('stage').getBoundingClientRect();
  const pad = 6, min = 44;
  let l = Math.max(stage.left, r.left - pad), t = Math.max(stage.top, r.top - pad);
  let rr = Math.min(stage.right, r.right + pad), b = Math.min(stage.bottom, r.bottom + pad);
  if (rr - l < min) { const cx = (l + rr) / 2; l = cx - min / 2; rr = cx + min / 2; }
  if (b - t < min) { const cy = (t + b) / 2; t = cy - min / 2; b = cy + min / 2; }
  box.style.left = (l - host.left) + 'px';
  box.style.top = (t - host.top) + 'px';
  box.style.width = (rr - l) + 'px';
  box.style.height = (b - t) + 'px';
  box.classList.toggle('scalable', moving && cat.IS_ZOOM != 0);
  box.classList.toggle('rotatable', moving && canRotate(cat));
  // 上に余白が無い（キャラの四角の上の端・上に浮かせたボタンの下）ときは、回すつまみは枠の中に
  const floats = Math.max.apply(null, (Array.prototype.map.call(document.querySelectorAll('.stage-area > .float'), (f: Element) => f.getBoundingClientRect().bottom) as number[]).concat([stage.top]));
  box.classList.toggle('rot-in', t - 34 < floats);
  box.classList.toggle('flash', !moving && !!S.boxFlash);
  box.hidden = false;
}
export function flashBox() {
  if (S.moveMode) return;
  clearTimeout(S.boxFlash);
  const box = $('partBox');
  box.classList.remove('flash');
  void box.offsetWidth; // 続けて押したときも、出し直す
  S.boxFlash = setTimeout(() => { S.boxFlash = 0; updateBox(); }, 900);
  updateBox();
}
// 回せるカテゴリ（CHOCO のデータには回転の可否が無いので、縦横に動かせるもの IS_MOVE 3 にした）
export function canRotate(cat: Category | undefined) { return !!cat && cat.IS_MOVE == 3; }
export function inBox(x: number, y: number) {
  const box = $('partBox');
  if (box.hidden || box.classList.contains('flash')) return false;
  const r = box.getBoundingClientRect(), m = 8;
  return x >= r.left - m && x <= r.right + m && y >= r.top - m && y <= r.bottom + m;
}
export function pickAt(x: number, y: number) {
  if (!S.layerKeyOf) {
    S.layerKeyOf = {};
    C.cats.forEach((c) => { c.LAYER.forEach((n) => { S.layerKeyOf![n] = keyOf(c); }); });
  }
  const keys: string[] = [];
  document.elementsFromPoint(x, y).forEach((n) => {
    const layer = n.closest && n.closest('g[id^="lcart_"]');
    if (!layer || !S.stageSvg.contains(layer)) return;
    const key = S.layerKeyOf![layer.id.slice(6)] && pickKey(S.layerKeyOf![layer.id.slice(6)]);
    if (key && key !== 'chara' && keys.indexOf(key) < 0) keys.push(key);
  });
  if (!keys.length) return;
  const i = keys.indexOf(S.selKey);
  selectCategory(i >= 0 ? keys[(i + 1) % keys.length] : keys[0]);
  flashBox();
}
export function bindBox() {
  let drag: BoxDrag | null = null;
  $('partBox').querySelectorAll<HTMLElement>('.h').forEach((h) => {
    // 回すつまみ: 枠のまん中から見た向きの変わった分だけ回す（0 度の近くでは 0 にくっつく）
    if (h.classList.contains('rot')) {
      h.addEventListener('pointerdown', (e) => {
        const cat = catByKey[S.selKey], st = C.state[S.selKey], r = partRect(S.selKey);
        if (!st || !canRotate(cat) || !r) return;
        e.preventDefault();
        e.stopPropagation();
        h.setPointerCapture(e.pointerId);
        const cx = (r.left + r.right) / 2, cy = (r.top + r.bottom) / 2, rev = Number(st.rev) === 1 && (cat.REVERSE == 1 || cat.IS_REV != 0);
        drag = { id: e.pointerId, rot: true, cx: cx, cy: cy, a0: Math.atan2(e.clientY - cy, e.clientX - cx), v0: (rev ? -1 : 1) * (Number(st.rotate) || 0), rev: rev } as BoxDrag;
        $('partBox').classList.add('rotating');
      });
      h.addEventListener('pointermove', (e) => {
        if (!drag || drag.id !== e.pointerId || !drag.rot) return;
        const cat = catByKey[S.selKey], st = C.state[S.selKey];
        let v = drag.v0 + (Math.atan2(e.clientY - drag.cy, e.clientX - drag.cx) - drag.a0) * 180 / Math.PI;
        v = ((v + 540) % 360) - 180;
        if (Math.abs(v) < 4) v = 0;
        v = Math.round(v);
        const stored = drag.rev ? -v : v;
        if (stored === (Number(st.rotate) || 0)) return;
        $('partBox').dataset.angle = v + '°';
        setRotate(cat, stored);
      });
      (['pointerup', 'pointercancel'] as const).forEach((type) => {
        h.addEventListener(type, (e) => {
          if (!drag || drag.id !== e.pointerId) return;
          drag = null;
          $('partBox').classList.remove('rotating');
          updateTools();
        });
      });
      return;
    }
    h.addEventListener('pointerdown', (e) => {
      const cat = catByKey[S.selKey], st = C.state[S.selKey], r = partRect(S.selKey);
      if (!st || !cat || cat.IS_ZOOM == 0 || !r) return;
      e.preventDefault();
      e.stopPropagation();
      h.setPointerCapture(e.pointerId);
      const cx = (r.left + r.right) / 2, cy = (r.top + r.bottom) / 2;
      drag = { id: e.pointerId, cx: cx, cy: cy, d0: Math.hypot(e.clientX - cx, e.clientY - cy) || 1, z0: Number(st.scale) || 1, tx0: Number(st.tx) || 0, ty0: Number(st.ty) || 0, m: layerCenter(cat) } as BoxDrag;
    });
    h.addEventListener('pointermove', (e) => {
      if (!drag || drag.id !== e.pointerId) return;
      const cat = catByKey[S.selKey], st = C.state[S.selKey];
      const z = Math.round(Math.max(1 - ZOOM_MAX, Math.min(1 + ZOOM_MAX, drag.z0 * Math.hypot(e.clientX - drag.cx, e.clientY - drag.cy) / drag.d0)) * 100) / 100;
      if (z === Number(st.scale)) return;
      let tx: number | null = null, ty: number | null = null;
      if (drag.m && cat.IS_MOVE != 0) {
        const k = (drag.z0 - z) / drag.z0, rev = Number(st.rev) === 1 && (cat.REVERSE == 1 || cat.IS_REV != 0);
        if (cat.IS_MOVE == 3) tx = Math.round(drag.tx0 + (rev ? -1 : 1) * (drag.m.x - SIZE / 2) * k);
        ty = Math.round(drag.ty0 + (drag.m.y - SIZE / 2) * k);
      }
      setScale(cat, z, tx, ty);
    });
    (['pointerup', 'pointercancel'] as const).forEach((type) => {
      h.addEventListener(type, (e) => {
        if (!drag || drag.id !== e.pointerId) return;
        drag = null;
        updateTools();
      });
    });
  });
  // キャラの四角の大きさが変わったら（シートを広げた・画面を回した）
  if (window.ResizeObserver) new ResizeObserver(scheduleBox).observe($('stage'));
  fitWithoutContainerQueries();
}
